import React, { useState, useRef } from "react";
import * as pdfjsLib from 'pdfjs-dist';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, FileText, CheckCircle, AlertCircle } from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export function PdfUploadDialog({ isOpen, onOpenChange, onBulkUpdate }) {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setError('');
    } else {
      setFile(null);
      setError('Por favor, selecciona un archivo PDF válido.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = async (filename) => {
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform());

    if (isNative) {
      try {
        const response = await fetch(`/templates/${filename}`);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: filename,
          files: [writeResult.uri],
        });
      } catch (err) {
        console.error('Error al descargar plantilla en Android:', err);
      }
      return;
    }

    const a = document.createElement('a');
    a.href = `/templates/${filename}`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const processPdf = async () => {
    if (!file) {
      setError('No se ha seleccionado ningún archivo.');
      return;
    }
    setIsProcessing(true);
    setError('');

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent({
            normalizeWhitespace: true,
        });

        let lastY, text = '';
        for (let item of textContent.items) {
            if (lastY == item.transform[5] || !lastY){
                text += item.str;
            } else {
                text += '\n' + item.str;
            }
            lastY = item.transform[5];
        }
        fullText += text + '\n';
      }
      await onBulkUpdate(fullText);
      onOpenChange(false);
      setFile(null);
    } catch (err) {
      console.error("Error processing PDF:", err);
      setError('Hubo un error al procesar el PDF. Asegúrate de que el formato es correcto.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleClose = () => {
    setFile(null);
    setError('');
    setIsProcessing(false);
    onOpenChange(false);
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Importar clientes desde PDF</DialogTitle>
           <DialogDescription className="space-y-3 text-sm">
             <span>Descarga la plantilla, rellénala y guárdala como PDF antes de subirla.</span>
             <div className="flex flex-col sm:flex-row gap-2 pt-1">
               <button
                 onClick={() => handleDownloadTemplate('plantilla_clientes_instrucciones_rutapp.docx')}
                 className="flex-1 inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-xs font-medium shadow-sm hover:bg-muted transition-colors"
               >
                  📄 Guardar instrucciones
               </button>
               <button
                 onClick={() => handleDownloadTemplate('plantilla_clientes_limpia_rutapp.docx')}
                 className="flex-1 inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-2 text-xs font-medium shadow-sm hover:bg-muted transition-colors"
               >
                  📋 Guardar plantilla limpia
               </button>
             </div>
              <span className="text-xs text-muted-foreground">El PDF final debe contener solo la lista de clientes. En Android se abrirá el menú para guardar, compartir o abrir la plantilla.</span>
           </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <Input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            onChange={handleFileChange} 
            accept=".pdf"
          />
          <div 
            className="border-2 border-dashed border-muted-foreground/50 rounded-lg p-8 w-full flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={handleUploadClick}
          >
            <UploadCloud className="h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {file ? 'Archivo seleccionado' : 'Haz clic o arrastra un archivo PDF aquí'}
            </p>
          </div>

          {file && (
            <div className="flex items-center justify-between p-2 bg-muted rounded-md">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
              </div>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm p-2 bg-destructive/10 rounded-md">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button onClick={processPdf} disabled={!file || isProcessing}>
            {isProcessing ? "Procesando..." : "Actualizar lista"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
